import { useEffect, useState } from "react";
import { runTransaction } from "firebase/database";
import { realNow, roomRef, setRoomPause } from "./room";

// The New Year countdown. Ten seconds before midnight on 31 December, by each
// player's own clock (their own time zone), a disco ball drops, a countdown
// runs, and at midnight come fireworks, the new year's number and music
// (components/NewYear.js). Anywhere on the site, every player gets it at
// their own midnight. In a room, it pauses the game for everyone there (see
// setRoomPause in room.js): friends in other time zones see the countdown
// at that player's midnight too, with who just entered the new year. It
// never shows anyone's place.

export const COUNTDOWN_MS = 10_000;
export const PARTY_MS = 8_000;

// Midnight at the start of the next year, by this device's clock.
export const nextNewYear = (now = new Date()) => new Date(now.getFullYear() + 1, 0, 1).getTime();

// What's showing: { from (server time the countdown started), year, names
// (who entered the new year, in a room) }, or null.
let show = null;
const listeners = new Set();
function setShow(value) {
  show = value;
  listeners.forEach((fn) => fn(value));
}
export const endShow = () => setShow(null);

export function useNewYearShow() {
  const [value, setValue] = useState(show);
  useEffect(() => {
    listeners.add(setValue);
    return () => listeners.delete(setValue);
  }, []);
  return value;
}

// The room this page is playing in, if any (set by pages/Room.js).
let room = null;
export function setNewYearRoom(value) {
  room = value;
  if (!value) setRoomPause(null);
}

// The room's countdown (meta/newYear) changed: pause the room's clock to
// match, and show it if it's still on.
export function roomNewYear(value) {
  setRoomPause(value);
  if (value && realNow() < value.to && show?.from !== value.from) {
    setShow({ from: value.from, year: value.year, names: Object.values(value.names || {}) });
  } else if (value && show?.from === value.from) {
    setShow({ ...show, names: Object.values(value.names || {}) });
  }
}

// The next countdown in a room: players whose midnight is the same moment
// join one countdown; a new one keeps count of how long the room was paused.
export function nextRoomCountdown(cur, { now, from, year, uid, name }) {
  if (cur && now < cur.to) return { ...cur, names: { ...cur.names, [uid]: name } };
  const before = cur ? (cur.before || 0) + (cur.to - cur.from) : 0;
  return { from, to: from + COUNTDOWN_MS + PARTY_MS, before, year, names: { [uid]: name } };
}

// This player's midnight is 10 seconds away (or less, if the page opened
// late): start the countdown, for the whole room if they're in one.
function start(target) {
  const year = new Date(target).getFullYear();
  const from = realNow() - (Date.now() - (target - COUNTDOWN_MS));
  const local = () => setShow({ from, year, names: null });
  if (!room) return local();
  const { code, uid, name } = room;
  runTransaction(roomRef(code, "meta/newYear"), (cur) => nextRoomCountdown(cur, { now: realNow(), from, year, uid, name })).catch(local);
}

// The admin page's preview: the countdown on this screen only.
export const previewNewYear = () => setShow({ from: realNow(), year: new Date().getFullYear() + 1, names: null });

// Waits for this device's next midnight on New Year's Eve. Runs once, for
// the whole site (App.js).
let started = false;
export function startNewYearClock() {
  if (started || typeof window === "undefined") return;
  started = true;
  const wait = () => {
    const target = nextNewYear();
    const ms = target - COUNTDOWN_MS - Date.now();
    // Timers can't wait more than about 24 days; check again later.
    setTimeout(
      () => {
        // A sleeping tab may wake up after midnight: then it's too late.
        if (Date.now() >= target - COUNTDOWN_MS && Date.now() < target) start(target);
        if (Date.now() >= target - COUNTDOWN_MS) setTimeout(wait, COUNTDOWN_MS + PARTY_MS);
        else wait();
      },
      Math.min(Math.max(0, ms), 2 ** 30),
    );
  };
  wait();
}
